// Global Pricing index page — lists all operation groups and lets the user
// navigate to the detail/edit view for each operation.
import { Box, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useInternalFeeOperationsQuery,
  useInternalRevenueOperationsQuery,
  useExternalFeeOperationsQuery,
  useExternalRevenueOperationsQuery,
} from './api';
import { OperationsBlock } from './components/OperationsBlock';
import { OperationCard } from './components/OperationCard';
import { OperationActionEnum, OperationGroupEnum } from '@/types/pricing';

export function GlobalPricingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const internalFeeQuery = useInternalFeeOperationsQuery();
  const internalRevenueQuery = useInternalRevenueOperationsQuery();
  const externalFeeQuery = useExternalFeeOperationsQuery();
  const externalRevenueQuery = useExternalRevenueOperationsQuery();

  const isLoading =
    internalFeeQuery.isLoading ||
    internalRevenueQuery.isLoading ||
    externalFeeQuery.isLoading ||
    externalRevenueQuery.isLoading;

  const handleAction = (
    action: OperationActionEnum,
    id: number,
    operationGroup: OperationGroupEnum,
    operationType: string,
  ) => {
    navigate(`/pricing/global/${action}/${operationGroup}/${operationType}/${id}`);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('pages.pricing.global_pricing.title', { defaultValue: 'Global Pricing' })}
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('pages.pricing.global_pricing.subtitle', {
            defaultValue: 'Manage the global pricing configuration for all operations.',
          })}
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Internal operations */}
            <Grid item xs={12} md={6}>
              {(internalFeeQuery.data?.length ?? 0) > 0 && (
                <OperationsBlock
                  blockHeader={t(
                    'pages.pricing.global_pricing.operations.with_internal_fees',
                    { defaultValue: 'Operations with internal fees' },
                  )}
                >
                  {internalFeeQuery.data!.map((op) => (
                    <OperationCard
                      key={op.id}
                      id={op.id}
                      nameKey={`pages.pricing.operation_type.${op.operationType.toLowerCase()}`}
                      onActionRequest={(action, id) =>
                        handleAction(
                          action,
                          id,
                          OperationGroupEnum.INTERNAL_FEE,
                          op.operationType,
                        )
                      }
                    />
                  ))}
                </OperationsBlock>
              )}

              {(internalRevenueQuery.data?.length ?? 0) > 0 && (
                <OperationsBlock
                  blockHeader={t(
                    'pages.pricing.global_pricing.operations.with_internal_revenues',
                    { defaultValue: 'Operations with internal revenues' },
                  )}
                >
                  {internalRevenueQuery.data!.map((op) => (
                    <OperationCard
                      key={op.id}
                      id={op.id}
                      nameKey={`pages.pricing.operation_type.${op.operationType.toLowerCase()}`}
                      onActionRequest={(action, id) =>
                        handleAction(
                          action,
                          id,
                          OperationGroupEnum.INTERNAL_REVENUE,
                          op.operationType,
                        )
                      }
                    />
                  ))}
                </OperationsBlock>
              )}
            </Grid>

            {/* External operations */}
            <Grid item xs={12} md={6}>
              <OperationsBlock
                blockHeader={t(
                  'pages.pricing.global_pricing.operations.with_external_fees',
                  { defaultValue: 'Operations with external fees' },
                )}
              >
                {(externalFeeQuery.data ?? []).map((op) => (
                  <OperationCard
                    key={op.nostroOperationId}
                    id={op.nostroOperationId}
                    nameKey={`pages.pricing.operation_type.${op.microOperationType.toLowerCase()}`}
                    providerName={op.providerName}
                    onActionRequest={(action, id) =>
                      handleAction(
                        action,
                        id,
                        OperationGroupEnum.EXTERNAL_FEE,
                        op.microOperationType,
                      )
                    }
                  />
                ))}
              </OperationsBlock>

              <OperationsBlock
                blockHeader={t(
                  'pages.pricing.global_pricing.operations.with_external_revenues',
                  { defaultValue: 'Operations with external revenues' },
                )}
              >
                {(externalRevenueQuery.data ?? []).map((op) => (
                  <OperationCard
                    key={op.nostroOperationId}
                    id={op.nostroOperationId}
                    nameKey={`pages.pricing.operation_type.${op.microOperationType.toLowerCase()}`}
                    providerName={op.providerName}
                    onActionRequest={(action, id) =>
                      handleAction(
                        action,
                        id,
                        OperationGroupEnum.EXTERNAL_REVENUE,
                        op.microOperationType,
                      )
                    }
                  />
                ))}
              </OperationsBlock>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Box>
  );
}
